import React from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist';
import { useContext, useState, useEffect } from 'react';
import { PriceLinePercentContext, FilteredSalesContext, SelectionContext, AllSalesDataContext, SuggestedPriceContext, AllInventoryDataContext, PredictedPriceDataContext } from './R application/ControlBar';
import { BatchExportQueueContext } from '../contexts/BatchExportQueueContext';
import ErrorMessagePopup from '../CommanService/ErrorMessagePopup';

/**
 * PricingCurvePage Component
 * 
 * This component displays a pricing curve chart with dynamic regression line calculation.
 * 
 * Key Features:
 * - Dynamic regression line calculated from filtered sales data (similar to R application)
 * - Uses exponential model: price = a * exp(-b * mileage)
 * - Regression line updates when interchange selection changes
 * - Handles edge cases (insufficient data, invalid coefficients)
 * - Includes debugging information for troubleshooting
 * - User mileage input with suggested price calculation (similar to R application)
 * 
 * Mileage Input Functionality (Same as R Application):
 * 1. User enters mileage in ControlBar (10,000-200,000 miles)
 * 2. System validates input range
 * 3. Calculates base price using regression model
 * 4. Applies quartile-based price line percentage
 * 5. Adds additional price line percentage
 * 6. Rounds final price to nearest $10
 * 7. Displays suggested price with detailed breakdown
 * 8. Shows gold star point on chart
 * 9. Clears when interchange changes
 * 
 * The regression calculation follows the same logic as the R application:
 * 1. Filters valid sales data (mileage > 0, price > 0, excludes certain brokered sales)
 * 2. Converts mileage to thousands (like R)
 * 3. Calculates exponential regression coefficients using least squares
 * 4. Generates regression line points for plotting
 * 5. Calculates suggested price for user-entered mileage
 */

// Static data for demonstration
// let staticInventory = [];

let PredictedData = []
let SalesData = []
let SalesDataMileage = []

// Function to update PredictedData from other components
window.updatePredictedData = function(data) {
  PredictedData = data;
  // staticInventory = data;
  console.log('PredictedData updated:', PredictedData);
}

// Function to update SalesData from ControlBar
window.updateSalesData = function(data) {
  SalesData = data;
  console.log('SalesData updated:', SalesData);
  
  // Extract mileage values into a new list
  SalesDataMileage = SalesData.map(item => ({
    mileage: item.mileage  // Keep original mileage values (already in thousands)
  }));
  
  console.log('SalesDataMileage extracted:', SalesDataMileage);
  
  // Make it available globally if needed
  window.SalesDataMileage = SalesDataMileage;
}
const staticSales = [ ];

// const staticSales = [
  // { mileage: 100000, price: 5000, days_instock: 180, type: 'InStock' },
  // { mileage: 110000, price: 5100, days_instock: 120, type: 'InStock' },
  // { mileage: 120000, price: 5200, days_instock: 90, type: 'Brokered' },
  // { mileage: 130000, price: 5300, days_instock: 60, type: 'InStock' },
  // { mileage: 140000, price: 5000, days_instock: 80, type: 'Brokered' },
  // { mileage: 150000, price: 4900, days_instock: 100, type: 'InStock' },
  // { mileage: 160000, price: 4800, days_instock: 70, type: 'Brokered' },
  // { mileage: 170000, price: 4700, days_instock: 60, type: 'InStock' },
  // { mileage: 180000, price: 4600, days_instock: 90, type: 'Brokered' },
  // { mileage: 190000, price: 4500, days_instock: 120, type: 'InStock' },
// ];

// Helper function to calculate exponential regression (similar to R's nls)
const calculateExponentialRegression = (salesData, allSalesData = null, interchangeNumber = null) => {
  if (!salesData || salesData.length < 5) {
    console.log('Not enough sales data for regression:', salesData?.length || 0);
    
    // Try fallback to all regions data for the same interchange (like R application)
    if (allSalesData && interchangeNumber) {
      console.log('Trying fallback to all regions data for interchange:', interchangeNumber);
      const allRegionData = allSalesData.filter(sale => 
        sale.interchange_number === interchangeNumber &&
        sale.mileage > 0 && 
        sale.all_parts_gross_sales > 0 && 
        !(sale.part_source === 'Brokered' && sale.mileage === 999)
      );
      
      if (allRegionData.length >= 5) {
        console.log('Using all regions data for regression:', allRegionData.length, 'points');
        return calculateRegressionFromData(allRegionData, true);
      }
    }
    
    return null;
  }

  return calculateRegressionFromData(salesData, false);
};

// Helper function to calculate regression from given data
const calculateRegressionFromData = (data, usingAllRegions = false) => {
  // Filter valid data points (similar to R logic)
  const validData = data.filter(sale => 
    sale.mileage > 0 && 
    sale.all_parts_gross_sales > 0 && 
    !(sale.part_source === 'Brokered' && sale.mileage === 999)
  );

  if (validData.length < 5) {
    console.log('Not enough valid data points after filtering:', validData.length);
    return null;
  }

  console.log('Calculating regression with', validData.length, 'data points', usingAllRegions ? '(using all regions)' : '(using selected region)');

  // Convert mileage to thousands (like in R)
  const regressionData = validData.map(sale => ({
    mileage: sale.mileage * 1000,
    price: sale.all_parts_gross_sales
  }));

  // Simple exponential regression using least squares
  // Model: price = a * exp(-b * mileage)
  const n = regressionData.length;
  const sumX = regressionData.reduce((sum, d) => sum + d.mileage, 0);
  const sumY = regressionData.reduce((sum, d) => sum + Math.log(d.price), 0);
  const sumXY = regressionData.reduce((sum, d) => sum + d.mileage * Math.log(d.price), 0);
  const sumX2 = regressionData.reduce((sum, d) => sum + d.mileage * d.mileage, 0);

  // Calculate coefficients
  const denominator = n * sumX2 - sumX * sumX;
  if (Math.abs(denominator) < 1e-10) {
    console.log('Regression calculation failed: denominator too small');
    return null;
  }

  const b = (n * sumXY - sumX * sumY) / denominator;
  const a = Math.exp((sumY - b * sumX) / n);

  // Validate coefficients
  if (!isFinite(a) || !isFinite(b) || a <= 0) {
    console.log('Invalid regression coefficients:', { a, b });
    return null;
  }

  console.log('Regression coefficients:', { a, b }, usingAllRegions ? '(using all regions)' : '(using selected region)');
  return { a, b, usingAllRegions };
};

// Helper function to generate regression line points
const generateRegressionLine = (model, minMileage = 40000, maxMileage = 200000, points = 100) => {
  if (!model) {
    console.log('No regression model available for line generation');
    return { x: [], y: [] };
  }

  if (!model.a || !isFinite(model.a) || !isFinite(model.b)) {
    console.log('Invalid model coefficients for line generation:', model);
    return { x: [], y: [] };
  }

  const step = (maxMileage - minMileage) / (points - 1);
  const x = [];
  const y = [];

  for (let i = 0; i < points; i++) {
    const mileage = minMileage + i * step;
    const price = model.a * Math.exp(-model.b * mileage);
    
    // Validate the calculated price
    if (isFinite(price) && price > 0) {
      x.push(mileage);
      y.push(price);
    }
  }

  console.log(`Generated regression line with ${x.length} points, range: ${Math.min(...y).toFixed(0)} - ${Math.max(...y).toFixed(0)}`);
  return { x, y };
};

const PricingCurvePage = ({ 
  moveToPriceLine, 
  processedSalesData = null, 
  showSoldParts = true, 
  includeBrokered = true 
}) => {
  const { priceLine1, priceLine2, priceLine3, priceLine4, additionalPriceLine, userMileage } = useContext(PriceLinePercentContext);
  const filteredSales = useContext(FilteredSalesContext);
  const { selectedInterchange, selectedMlModel, selectedVariable, selectedRegion } = useContext(SelectionContext);
  const allSalesData = useContext(AllSalesDataContext);
  const { suggestedPrice, setSuggestedPrice } = useContext(SuggestedPriceContext);
  const { predictedPriceData, setPredictedPriceData } = useContext(PredictedPriceDataContext);
  const allInventoryData = useContext(AllInventoryDataContext);
  const { setBatchExportQueue } = useContext(BatchExportQueueContext);

  // Use processed sales data if provided, otherwise fall back to context
  const salesDataForAnalysis = processedSalesData || filteredSales;

  // Add state for price calculation details
  const [priceCalculationDetails, setPriceCalculationDetails] = useState(null);

  // Debug: Log filtered sales data
  console.log('Sales data for regression:', {
    totalSales: salesDataForAnalysis?.length || 0,
    sampleData: salesDataForAnalysis?.slice(0, 3) || [],
    hasValidData: salesDataForAnalysis?.some(sale => 
      sale.mileage > 0 && 
      sale.all_parts_gross_sales > 0 && 
      !(sale.part_source === 'Brokered' && sale.mileage === 999)
    ),
    validDataCount: salesDataForAnalysis?.filter(sale => 
      sale.mileage > 0 && 
      sale.all_parts_gross_sales > 0 && 
      !(sale.part_source === 'Brokered' && sale.mileage === 999)
    ).length || 0,
    dataStructure: salesDataForAnalysis?.length > 0 ? Object.keys(salesDataForAnalysis[0]) : [],
    usingProcessedData: !!processedSalesData,
    showSoldParts,
    includeBrokered
  });

  // Calculate dynamic regression line from filtered sales data with fallback to all regions
  const regressionModel = calculateExponentialRegression(salesDataForAnalysis, allSalesData, selectedInterchange);
  const regressionLine = generateRegressionLine(regressionModel);

  // Debug: Log regression model information
  if (regressionModel) {
    console.log('Regression model info:', {
      usingAllRegions: regressionModel.usingAllRegions,
      coefficients: { a: regressionModel.a, b: regressionModel.b },
      dataSource: regressionModel.usingAllRegions ? 'All regions' : 'Selected region'
    });
  }

  // Helper function to calculate suggested price (similar to R application logic)
  const calculateSuggestedPrice = (mileage) => {
    if (!regressionModel || !regressionModel.a || !regressionModel.b) {
      return null;
    }

    // Validate mileage range (same as R: 10,000 to 200,000)
    if (mileage < 10000 || mileage > 200000) {
      return null;
    }

    // Calculate base price using regression model (same as R)
    const basePrice = regressionModel.a * Math.exp(-regressionModel.b * mileage);

    // Get quartile breaks for price line percentage calculation
    const quartiles = getQuartilesFromSalesData();
    
    // Calculate price line percentage based on mileage quartiles (same as R)
    const priceLinePercent = getPriceLinePercentForMileage(mileage, quartiles);
    
    // Calculate final price with additional percentage (same as R)
    const totalPercent = priceLinePercent + additionalPriceLine;
    const finalPrice = basePrice * (1 + totalPercent / 100);
    
    // Round to nearest $10 (same as R)
    const roundedPrice = Math.round(finalPrice / 10) * 10;

    return {
      basePrice: Math.round(basePrice),
      priceLinePercent,
      additionalPercent: additionalPriceLine,
      totalPercent,
      finalPrice: roundedPrice,
      calculationDetails: {
        mileage,
        modelA: regressionModel.a,
        modelB: regressionModel.b,
        quartiles,
        usingAllRegions: regressionModel.usingAllRegions
      }
    };
  };

  // Helper function to get quartiles from sales data (same as R)
  const getQuartilesFromSalesData = () => {
    if (!salesDataForAnalysis || salesDataForAnalysis.length === 0) {
      return [0, 0, 0, 0, 0];
    }

    const validSales = salesDataForAnalysis.filter(sale => 
      sale.mileage > 0 && 
      sale.all_parts_gross_sales > 0 && 
      !(sale.part_source === 'Brokered' && sale.mileage === 999)
    );

    if (validSales.length === 0) {
      return [0, 0, 0, 0, 0];
    }

    const sortedMileages = validSales.map(sale => sale.mileage).sort((a, b) => a - b);
    
    const getQuantile = (p) => {
      const pos = (sortedMileages.length - 1) * p;
      const base = Math.floor(pos);
      const rest = pos - base;
      if (sortedMileages[base + 1] !== undefined) {
        return sortedMileages[base] + rest * (sortedMileages[base + 1] - sortedMileages[base]);
      } else {
        return sortedMileages[base];
      }
    };

    return [
      getQuantile(0),   // Q0 (min)
      getQuantile(0.25), // Q1
      getQuantile(0.5),  // Q2 (median)
      getQuantile(0.75), // Q3
      getQuantile(1)     // Q4 (max)
    ];
  };

  // Helper function to get price line percentage based on mileage (same as R)
  const getPriceLinePercentForMileage = (mileage, quartiles) => {
    if (quartiles.length !== 5 || quartiles.some(q => isNaN(q))) {
      return 0;
    }

    // Same logic as R application
    if (mileage <= quartiles[1]) return priceLine1;
    if (mileage <= quartiles[2]) return priceLine2;
    if (mileage <= quartiles[3]) return priceLine3;
    return priceLine4;
  };

  // Handle mileage input changes from ControlBar context
  useEffect(() => {
    const mileage = parseFloat(userMileage);
    if (!isNaN(mileage) && mileage >= 0 ) {
      const priceInfo = calculateSuggestedPrice(mileage);
      setSuggestedPrice(priceInfo);
      setPriceCalculationDetails(priceInfo?.calculationDetails);
      
      // Debug logging (same as R application)
      console.log('=== Mileage Input Calculation ===');
      console.log('User entered mileage:', mileage);
      console.log('Price calculation result:', priceInfo);
      console.log('Regression model used:', regressionModel ? 'Available' : 'Not available');
      console.log('Using all regions data:', priceInfo?.calculationDetails?.usingAllRegions);
      console.log('================================');
    } else {
      setSuggestedPrice(null);
      setPriceCalculationDetails(null);
      setPredictedPriceData(null);
      if (userMileage) {
        console.log('Mileage input invalid or out of range:', userMileage);
      }
    }
  }, [userMileage, regressionModel, additionalPriceLine, priceLine1, priceLine2, priceLine3, priceLine4]);

  // Clear suggested price when interchange changes (same as R application behavior)
  useEffect(() => {
    setSuggestedPrice(null);
    setPriceCalculationDetails(null);
    setPredictedPriceData(null);
    console.log('Interchange changed, cleared suggested price and API predicted price');
  }, [selectedInterchange]);

  // Dynamic color bar range
  const daysInStockVals = PredictedData.map(d => d.days_instock);
  const cmin = Math.min(...daysInStockVals);
  const cmax = Math.max(...daysInStockVals);
  // Calculate 4 dynamic ticks
  const tick1 = cmin;
  const tick2 = Math.round(cmin + (cmax - cmin) / 3);
  const tick3 = Math.round(cmin + 2 * (cmax - cmin) / 3);
  const tick4 = cmax;
  const colorbarTicks = [tick1, tick2, tick3, tick4];

  // Helper: get quartiles from static sales data (same as in ControlBar)
  const staticSalesData = [];
  
  function getQuartiles(data) {
    if (!data || data.length === 0) return [0, 0, 0, 0, 0];
    const sorted = data.map(d => d.mileage).sort((a, b) => a - b);
    const q = p => {
      const pos = (sorted.length - 1) * p;
      const base = Math.floor(pos);
      const rest = pos - base;
      if (sorted[base + 1] !== undefined) {
        return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
      } else {
        return sorted[base];
      }
    };
    return [q(0), q(0.25), q(0.5), q(0.75), q(1)];
  }
  const quartiles = getQuartiles(SalesDataMileage);

  // Helper: get price line % for a given mileage
  function getPriceLinePercent(mileage) {
    if (mileage <= quartiles[1]) return priceLine1;
    if (mileage <= quartiles[2]) return priceLine2;
    if (mileage <= quartiles[3]) return priceLine3;
    return priceLine4;
  }

  // Ensure each item in PredictedData has predicted_price_raw
  const inventoryWithRaw = PredictedData.map(item => ({
    ...item,
    predicted_price_raw: item.predicted_price_raw !== undefined ? item.predicted_price_raw : item.predicted_price
  }));

  // Calculate adjusted_predicted_price for each inventory item
  const inventoryWithAdjusted = inventoryWithRaw.map(item => {
    const percent = getPriceLinePercent(item.mileage) + additionalPriceLine;
    return {
      ...item,
      adjusted_predicted_price: item.predicted_price_raw * (1 + percent / 100)
    };
  });

  // Create exact copy of the fully loaded inventoryWithAdjusted list with renamed fields and stock status
  const BatchExportQueue = inventoryWithAdjusted.map(item => {
    // Get stock status from allInventoryData
    const matchingStock = allInventoryData.find(stockItem => 
      stockItem.region === selectedRegion && 
      stockItem.interchange_number === selectedInterchange
    );
    const stockStatus = matchingStock?.stock_status || 'Adequately Stocked'; // Default fallback
    
    return {
      ...item,
      new_price: item.adjusted_predicted_price,
      region: selectedRegion,
      interchange_number: selectedInterchange,
      stock_status: stockStatus
    };
  });
  console.log('BatchExportQueue (exact copy of inventoryWithAdjusted with renamed fields and stock status):', BatchExportQueue);

  // Function to handle Queue Price Changes button
  const handleQueuePriceChanges = () => {
    if (moveToPriceLine) {
      setBatchExportQueue(BatchExportQueue);
      console.log('BatchExportQueue data queued:', BatchExportQueue);
    } else {
      console.log('Move inventory to price line checkbox is not checked');
    }
  };

  // Expose the function globally so it can be called from ControlBar
  useEffect(() => {
    window.handleQueuePriceChanges = handleQueuePriceChanges;
    return () => {
      delete window.handleQueuePriceChanges;
    };
  }, [BatchExportQueue, moveToPriceLine]);

  
  // For both inventory dots (when checked) and price line curve, use the same arrays
  const alignedMileage = inventoryWithAdjusted.map(item => item.mileage);
  const alignedAdjustedPrice = inventoryWithAdjusted.map(item => item.adjusted_predicted_price);

  // For inventory dots
  const inventoryY = moveToPriceLine
    ? alignedAdjustedPrice
    : inventoryWithAdjusted.map(item => item.part_price);

  // For the price line curve, use mileage vs adjusted_predicted_price from inventory only
  const sortedInventory = [...inventoryWithAdjusted].sort((a, b) => a.mileage - b.mileage);
  const priceLineX = sortedInventory.map(item => item.mileage);
  const priceLineY = sortedInventory.map(item => item.adjusted_predicted_price);

  // Extrapolate one extra point for the price line (end)
  const lastMileage = priceLineX[priceLineX.length - 1];
  const lastPredicted = priceLineY[priceLineY.length - 1];
  const secondLastMileage = priceLineX[priceLineX.length - 2];
  const secondLastPredicted = priceLineY[priceLineY.length - 2];
  const extraMileageEnd = lastMileage + 0.1 * lastMileage;
  const slopeEnd = (lastPredicted - secondLastPredicted) / (lastMileage - secondLastMileage);
  const extrapolatedPredictedEnd = lastPredicted + slopeEnd * (extraMileageEnd - lastMileage);

  // Extrapolate one extra point for the price line (start)
  const firstMileage = priceLineX[0];
  const firstPredicted = priceLineY[0];
  const secondMileage = priceLineX[1];
  const secondPredicted = priceLineY[1];
  const extraMileageStart = firstMileage - 0.1 * firstMileage;
  const slopeStart = (secondPredicted - firstPredicted) / (secondMileage - firstMileage);
  const extrapolatedPredictedStart = firstPredicted + slopeStart * (extraMileageStart - firstMileage);

  const extendedPriceLineX = [extraMileageStart, ...priceLineX, extraMileageEnd];
  const extendedPriceLineY = [extrapolatedPredictedStart, ...priceLineY, extrapolatedPredictedEnd];

  // Gather all relevant Y values for axis calculation
  const allYValues = moveToPriceLine 
    ? [
        ...inventoryWithAdjusted.map(d => d.adjusted_predicted_price),
      ]
    : [
        ...inventoryWithAdjusted.map(d => d.part_price),
        ...inventoryWithAdjusted.map(d => d.adjusted_predicted_price),
      ];
  // Gather all relevant X values for axis calculation
  const allXValues = [
    ...inventoryWithAdjusted.map(d => d.mileage),
  ];

  // Add sales data values for axis calculation to ensure cross plots are visible
  if (processedSalesData && processedSalesData.length > 0) {
    const salesXValues = processedSalesData.map(sale => sale.mileage_plot);
    const salesYValues = processedSalesData.map(sale => sale.adjusted_price);
    
    allXValues.push(...salesXValues);
    allYValues.push(...salesYValues);
    
    console.log('Sales data added to axis calculation:', {
      salesCount: processedSalesData.length,
      salesXRange: salesXValues.length > 0 ? `${Math.min(...salesXValues)} - ${Math.max(...salesXValues)}` : 'N/A',
      salesYRange: salesYValues.length > 0 ? `${Math.min(...salesYValues)} - ${Math.max(...salesYValues)}` : 'N/A'
    });
  }

  // Add regression line values if available
  // COMMENTED OUT: Regression line values from axis calculation
  /*
  if (regressionLine.x.length > 0 && regressionLine.y.length > 0) {
    allYValues.push(...regressionLine.y);
    allXValues.push(...regressionLine.x);
    console.log('Regression line Y range:', Math.min(...regressionLine.y).toFixed(0), '-', Math.max(...regressionLine.y).toFixed(0));
  }
  */

  // // Add suggested price point values if available
  // if (suggestedPrice && suggestedPrice.finalPrice) {
  //   allYValues.push(suggestedPrice.finalPrice);
  //   allXValues.push(suggestedPrice.calculationDetails.mileage);
  //   console.log('Suggested price point added to axis calculation:', {
  //     x: suggestedPrice.calculationDetails.mileage,
  //     y: suggestedPrice.finalPrice
  //   });
  // }

  // Add API predicted price point values if available
  if (predictedPriceData && predictedPriceData.mileage && predictedPriceData.predicted_price) {
    allYValues.push(predictedPriceData.predicted_price);
    allXValues.push(predictedPriceData.mileage);
    console.log('API predicted price point added to axis calculation:', {
      x: predictedPriceData.mileage,
      y: predictedPriceData.predicted_price
    });
  }

  // Calculate min/max with better padding to ensure regression line is visible
  const minX = allXValues.length > 0 ? Math.min(...allXValues) - 0.05 * Math.min(...allXValues) : 40000;
  const maxX = allXValues.length > 0 ? Math.max(...allXValues) + 0.05 * Math.max(...allXValues) : 200000;
  
  // Ensure Y-axis includes regression line with generous padding
  const minY = allYValues.length > 0 ? Math.min(...allYValues) - 0.1 * Math.min(...allYValues) : 3000;
  const maxY = allYValues.length > 0 ? Math.max(...allYValues) + 0.1 * Math.max(...allYValues) : 6000;

  console.log('Axis ranges - X:', minX.toFixed(0), '-', maxX.toFixed(0), 'Y:', minY.toFixed(0), '-', maxY.toFixed(0));

  const legendBoxStyle = {
    position: 'absolute',
    top: '10px',
    left: '10px',
    background: 'white',
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    zIndex: 10,
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    color: '#333',
    width: '200px',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  };

  const markerStyle = {
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '8px',
  };

  // Prepare plot data array
  const plotData = [
          // Inventory dots (circular, colored by days_instock)
          // Hover tooltip shows: Stock #, Unique ID, Mileage, Original Price (part_price), 
          // Predicted Price (adjusted_predicted_price), Percentage Change, Days In Stock
          {
            x: alignedMileage,
            y: inventoryY,
            mode: 'markers',
            marker: {
              color: daysInStockVals,
              colorscale: [
                [0, 'green'],
                [0.25, 'lightgreen'],
                [0.5, 'orange'],
                [1, 'red']
              ],
              cmin,
              cmax,
              size: 16,
              symbol: 'circle',
              line: { width: 2, color: 'black' },
              showscale: true,
              colorbar: {
                title: '',
                titleside: 'right',
                tickvals: colorbarTicks,
                ticktext: colorbarTicks.map(val => `- ${val}`),
                len: 0.5,
                y: 0.75,
                yanchor: 'middle',
                outlinewidth: 1,
                outlinecolor: 'black',
                thickness: 30,
                x: 1.05
              }
            },
            name: 'Inventory',
            showlegend: false,
            hovertemplate: 'Stock #: %{customdata[0]}<br>Unique ID: %{customdata[1]}<br>Mileage: %{x}<br>Original Price: $%{customdata[2]}<br>Predicted Price: $%{customdata[3]}<br>Percentage Change: %{customdata[4]}%<br>Days In Stock: %{marker.color}<extra></extra>',
            customdata: inventoryWithAdjusted.map(item => [
              item.stock_number || 'N/A',
              item.unique_id || 'N/A',
              item.part_price?.toFixed(2) || 'N/A',
              item.adjusted_predicted_price?.toFixed(2) || 'N/A',
              item.adjusted_predicted_price && item.part_price 
                ? ((item.adjusted_predicted_price - item.part_price) / item.part_price * 100).toFixed(1)
                : 'N/A'
            ])
          },
          // Sold (InStock) points (x, colored by days_instock)
          ...(showSoldParts && salesDataForAnalysis ? salesDataForAnalysis
            .filter(sale => sale.part_source !== 'Brokered')
            .map((sale, index) => ({
              x: [sale.mileage_plot],
              y: [sale.adjusted_price],
              mode: 'markers',
              marker: {
                color: sale.plot_color,
                size: 14,
                symbol: sale.plot_symbol,
                showscale: false
              },
              name: 'Sold (InStock)',
              hovertemplate: sale.hover_text,
              showlegend: index === 0 // Only show legend for first occurrence
            })) : []),
          // Sold (Brokered) points (x, colored by days_instock)
          ...(showSoldParts && includeBrokered && salesDataForAnalysis ? salesDataForAnalysis
            .filter(sale => sale.part_source === 'Brokered')
            .map((sale, index) => ({
              x: [sale.mileage_plot],
              y: [sale.adjusted_price],
              mode: 'markers',
              marker: {
                color: sale.plot_color,
                size: 14,
                symbol: sale.plot_symbol,
                showscale: false
              },
              name: 'Sold (Brokered)',
              hovertemplate: sale.hover_text,
              showlegend: index === 0 // Only show legend for first occurrence
            })) : []),
          // Price Line (dashed curve) based on predicted_price
          {
            x: extendedPriceLineX,
            y: extendedPriceLineY,
            mode: 'lines',
            line: { color: '#ff9900', width: 3, dash: 'dash', shape: 'spline' },
            name: 'Price Line (+Additional line %)',
            showlegend: true,
    }
  ];

  // Add regression line if it was successfully calculated
  // COMMENTED OUT: Regression line display in UI
  /*
  if (regressionModel && regressionLine.x.length > 0) {
    // Insert regression line after inventory but before sales data
    plotData.splice(1, 0, {
      x: regressionLine.x,
      y: regressionLine.y,
      mode: 'lines',
      line: { 
        color: '#ff6600', // More distinct orange color
        width: 6, // Thicker line for better visibility
        shape: 'linear' // Ensure smooth line
      },
      name: 'Regression Line',
      showlegend: true,
      hovertemplate: 'Mileage: %{x}<br>Predicted Price: $%{y:.0f}<extra></extra>'
    });
    console.log('Regression line added to plot with', regressionLine.x.length, 'points');
    console.log('Regression line data sample:', {
      firstPoint: { x: regressionLine.x[0], y: regressionLine.y[0] },
      lastPoint: { x: regressionLine.x[regressionLine.x.length - 1], y: regressionLine.y[regressionLine.y.length - 1] }
    });
  } else {
    console.log('Regression line not available - insufficient data');
  }
  */

  // COMMENTED OUT: Add suggested price point if available (same as R application)
  /*
  if (suggestedPrice && suggestedPrice.finalPrice) {
      plotData.push({
      x: [suggestedPrice.calculationDetails.mileage],
      y: [suggestedPrice.finalPrice],
      mode: 'markers',
      marker: {
        color: '#FFD700',
        size: 18,
        symbol: 'asterisk',
        line: { width: 2, color: 'black' }
      },
      name: 'Suggested Price',
      showlegend: true,
      hovertemplate: `Mileage: ${suggestedPrice.calculationDetails.mileage.toLocaleString()}<br>Suggested Price: $${suggestedPrice.finalPrice.toLocaleString()}<br>Base Price: $${suggestedPrice.basePrice.toLocaleString()}<br>Price Line %: ${suggestedPrice.priceLinePercent}%<br>Additional %: ${suggestedPrice.additionalPercent}%<br>Total %: ${suggestedPrice.totalPercent}%<extra></extra>`
    });
    console.log('Suggested price point added to plot:', {
      mileage: suggestedPrice.calculationDetails.mileage,
      price: suggestedPrice.finalPrice
    });
  }
  */

  // Add API predicted price point if available
  if (predictedPriceData && predictedPriceData.mileage && predictedPriceData.predicted_price) {
    plotData.push({
      x: [predictedPriceData.mileage],
      y: [predictedPriceData.predicted_price],
      mode: 'markers',
      marker: {
        color: '#FFD700',
        size: 22,
        symbol: 'asterisk',
        line: { width: 3, color: 'yellow' }
      },
      name: 'Predicted Price',
      showlegend: true,
      hovertemplate: `Mileage: ${predictedPriceData.mileage.toLocaleString()}<br>Predicted Price: $${predictedPriceData.predicted_price.toLocaleString()}<extra></extra>`
    });
    console.log('API predicted price point added to plot:', {
      mileage: predictedPriceData.mileage,
      price: predictedPriceData.predicted_price
    });
  }

  // Determine if we should show the fallback message
  // COMMENTED OUT: Fallback message logic for regression line errors
  /*
  const showFallbackMessage = !selectedInterchange || !selectedMlModel || !selectedVariable || (!regressionModel && regressionLine.x.length === 0);
  
  // Get the reason for showing fallback message
  const getFallbackReason = () => {
    if (!selectedInterchange) {
      return "Please select an interchange to view the pricing curve.";
    }
    
    if (!selectedMlModel) {
      return "Please select an ML Model";
    }
    
    if (!selectedVariable) {
      return "Please select a Variable";
    }
    
    // Check if we have any sales data at all
    const allRegionData = allSalesData?.filter(sale => 
      sale.interchange_number === selectedInterchange &&
      sale.mileage > 0 && 
      sale.all_parts_gross_sales > 0 && 
      !(sale.part_source === 'Brokered' && sale.mileage === 999)
    ) || [];
    
    if (allRegionData.length === 0) {
      return `No sales data available for interchange ${selectedInterchange} across all regions.`;
    }
    
    if (allRegionData.length < 5) {
      return `Not enough sales data points available for interchange ${selectedInterchange} across all regions.`;
    }
    
    return `Unable to calculate regression curve for interchange ${selectedInterchange} with available data.`;
  };

  // Get the appropriate sub-message based on validation type
  const getFallbackSubMessage = () => {
    if (!selectedMlModel || !selectedVariable) {
      return "For prediction or training";
    }
    if (!selectedInterchange) {
      return "Please select a different interchange or region with more sales data.";
    }
    return "Please select a different interchange or region with more sales data.";
  };
  */

  const Plot = createPlotlyComponent(Plotly);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Show fallback message when no regression data is available */}
      {/* COMMENTED OUT: Error popup for regression line issues
      <ErrorMessagePopup
        message={getFallbackReason()}
        subMessage={getFallbackSubMessage()}
        isVisible={showFallbackMessage}
        customPosition="validation"
      />
      */}
      
      <Plot
        data={plotData}
        layout={{
          title: {
            text: selectedInterchange 
              ? `Interchange ${selectedInterchange} - Price vs. Mileage`
              // COMMENTED OUT: "Using sales data from all regions" text
              // ? `Interchange ${selectedInterchange} - Price vs. Mileage${regressionModel?.usingAllRegions ? ' (Using sales data from all regions)' : ''}`
              : 'Select an Interchange to View Pricing Curve',
            font: { size: 24 },
            y: 0.93
          },
          xaxis: {
            title: { text: 'Mileage (mi)', font: { size: 18 } },
            range: [minX, maxX],
            showgrid: true,
            zeroline: false,
            tickformat: ',d',
          },
          yaxis: {
            title: { text: 'Price ($)', font: { size: 18 } },
            range: [minY, maxY],
            showgrid: true,
            zeroline: false
          },
          legend: {
            x: 1.05,
            y: 0.1,
            bgcolor: 'rgba(255,255,255,0.7)',
            bordercolor: '#ccc',
            borderwidth: 1
          },
          margin: { t: 60, l: 60, r: 60, b: 60 },
          plot_bgcolor: '#fff',
          paper_bgcolor: '#fff7f0',
          annotations: [
            {
              x: 1.22,
              y: 1.05,
              xref: 'paper',
              yref: 'paper',
              text: '<b>Days in stock</b>',
              showarrow: false,
              font: { size: 14 },
              align: 'center',
            },
            {
              x: 1.22,
              y: 0.4,
              xref: 'paper',
              yref: 'paper',
              text: '<b>Days in stock</b>',
              showarrow: false,
              font: { size: 14 },
              align: 'center',
            }
          ],
        }}
        config={{ displayModeBar: true, displaylogo: false, showlegend: true }}
        style={{ width: '100%', height: 500 }}
      />
    </div>
  );
};

export default PricingCurvePage; 